import { Component, EventEmitter, Input, Output, HostListener, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';

import { GraphService } from '~/core/services/graph.service';
import { AppConfigService } from '~/core/services/appconfig.service';
import { AppSettingsService } from '~/core/services/appsettings.service';
import { AuthService } from '~/core/services/auth.service';
import { environment } from '../../../../../environments/environment';

import { AppSettings } from '~/core/models/appsettings';

@Component({
    selector: 'header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})

export class HeaderComponent {
    @Input() title = "Digital Core — AI Assisted Backlog Generation Demo";
    @Output() setLayoutCssClass = new EventEmitter();
    @Output() reloadPage = new EventEmitter();
    @ViewChild('workspaceSwitcherModal') workspaceSwitcherModal!: ElementRef;

    public appSettings: AppSettings = new AppSettings();
    public env: string = "";
    public originalEnvFromConfig: string = ""; // Store original value from config.json
    public itemsClass = "";
    public initials: string = "";
    public showPhoto: boolean = false;
    public isLoggedIn: boolean = false;
    public showWorkspaceSwitcher: boolean = false;

    constructor(
        private router: Router,
        private graphService: GraphService,
        private appConfigService: AppConfigService,
        private appSettingsService: AppSettingsService,
        private authService: AuthService
    ) {
        console.log('[HeaderComponent] Constructor called');
        const configuredEnvironment = this.appConfigService.getConfig("environment");
        
        // Store the EXACT original value from config.json
        this.originalEnvFromConfig = configuredEnvironment; 
        // If config.json has empty string, use "localhost", otherwise use configured value or fallback to environment.name
        let resolvedEnvironment: string;
        if (configuredEnvironment === "") {
            resolvedEnvironment = "localhost";
            console.log('[HeaderComponent] Empty string detected, using "localhost"');
        } else {
            resolvedEnvironment = (configuredEnvironment || environment.name || "").toString().trim();
        }
        console.log('[HeaderComponent] Resolved environment (after fallback & trim):', resolvedEnvironment);
        
        if (resolvedEnvironment) {
            this.env = resolvedEnvironment.toLocaleUpperCase();
            console.log('[HeaderComponent] Final env value (uppercase):', this.env);
        } else {
            console.log('[HeaderComponent] No environment value set, this.env remains:', this.env);
        }
    }

    //*************************************************************************
    //  Component Life-Cycle Methods
    //*************************************************************************
    public async ngOnInit() {
        console.log('[HeaderComponent.ngOnInit] Component initialized, env value:', this.env);
        await this.getUserInfo();
    }

    //*************************************************************************
    //  Public Methods
    //*************************************************************************
    public navigateTo(url: string) {
        this.router.navigateByUrl(url);
    }

    public toggleWorkspaceSwitcher(): void {
        console.log('[HeaderComponent] toggleWorkspaceSwitcher - Current state:', this.showWorkspaceSwitcher);
        this.showWorkspaceSwitcher = !this.showWorkspaceSwitcher;
    }

    public closeWorkspaceSwitcher(): void {
        console.log('[HeaderComponent] closeWorkspaceSwitcher - Closing workspace switcher');
        this.showWorkspaceSwitcher = false;
    }

    /**
     * Close workspace switcher when clicking outside
     */
    @HostListener('document:click', ['$event'])
    public onDocumentClick(event: MouseEvent): void {
        // Check if click is outside the workspace switcher modal
        if (this.workspaceSwitcherModal && this.showWorkspaceSwitcher) {
            const clickedInside = this.workspaceSwitcherModal.nativeElement.contains(event.target as Node);
            const clickedOnTrigger = (event.target as HTMLElement).closest('.user') !== null;
            
            // Close if clicked outside and not on the trigger button
            if (!clickedInside && !clickedOnTrigger) {
                this.closeWorkspaceSwitcher();
            }
        }
    }

    //*************************************************************************
    //  Private Methods
    //*************************************************************************
    private async getUserInfo() {
        this.appSettings = await this.appSettingsService.getSettings();

        // Check if user is logged in via AuthService
        if (this.authService.isAuthenticated) {
            this.isLoggedIn = true;

            // If no user data from MSAL, use default developer user
            if (!this.appSettings.user || !this.appSettings.user.name) {
                this.appSettings.user = {
                    name: 'John Doe',
                    email: 'developer@ndaassist.com',
                    photo: '',
                    initials: 'JD'
                };
                this.appSettingsService.saveSettings(this.appSettings);
            }
        }

        if (this.appSettings.user?.photo !== "") {
            this.showPhoto = true;
        } else {
            this.graphService.getUserPhoto().then((photo: any) => {
                this.appSettings.user.photo = photo;

                if (photo !== "") {
                    this.showPhoto = true;
                }
    
                this.appSettingsService.saveSettings(this.appSettings);
            });
        }
    }
}
